import { UserPlus, UserMinus, UserCog } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onWelcome: (v: GuildConfig["welcome"]) => void;
  onGoodbye: (v: GuildConfig["goodbye"]) => void;
  onAutorole: (v: GuildConfig["autorole"]) => void;
}

const PLACEHOLDERS_HINT =
  "Placeholders: {user}=mention, {username}=name, {server}=server name, {count}=member count.";

export function WelcomeTab({
  config,
  channels,
  roles,
  onWelcome,
  onGoodbye,
  onAutorole,
}: Props) {
  const wc = config.welcome;
  const gb = config.goodbye;
  const ar = config.autorole;

  const updW = <K extends keyof typeof wc>(k: K, v: (typeof wc)[K]) =>
    onWelcome({ ...wc, [k]: v });
  const updG = <K extends keyof typeof gb>(k: K, v: (typeof gb)[K]) =>
    onGoodbye({ ...gb, [k]: v });
  const updA = <K extends keyof typeof ar>(k: K, v: (typeof ar)[K]) =>
    onAutorole({ ...ar, [k]: v });

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          These sections control <strong>automatic</strong> behavior—no member slash commands. On join/leave the bot
          sends your embeds if enabled; <strong>Autorole</strong> adds roles when someone joins (unless Security
          verification defers autorole until verify). Use placeholders in titles/messages as shown in each section.
        </p>
      </div>

      {/* WELCOME */}
      <Section
        icon={UserPlus}
        title="Welcome (join)"
        description="When someone joins, the bot sends a nice embed to the channel."
        enabled={wc.enabled}
        onToggle={(v) => updW("enabled", v)}
      >
        <Field
          label="Channel"
          hint="Where the welcome embed is sent (usually #welcome, #joins, or an announcements channel)."
        >
          <ChannelPicker
            channels={channels}
            value={wc.channel_id}
            onChange={(v) => updW("channel_id", v === "__none__" ? "" : v)}
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Embed title">
            <Input
              value={wc.title}
              onChange={(e) => updW("title", e.target.value)}
              placeholder="👋 Welcome, {user}!"
              maxLength={256}
            />
          </Field>
          <Field label="Color (HEX)">
            <div className="flex gap-2">
              <input
                type="color"
                value={wc.color || "#5865F2"}
                onChange={(e) => updW("color", e.target.value)}
                className="h-10 w-16 rounded-md border border-border bg-background cursor-pointer"
              />
              <Input
                value={wc.color}
                onChange={(e) => updW("color", e.target.value)}
                maxLength={7}
                className="flex-1"
              />
            </div>
          </Field>
        </div>

        <Field label="Message" hint={PLACEHOLDERS_HINT}>
          <Textarea
            value={wc.message}
            onChange={(e) => updW("message", e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="You joined {server}! We now have {count} members."
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <div className="text-sm font-medium">Show avatar</div>
              <div className="text-xs text-muted-foreground">
                User avatar as the embed thumbnail.
              </div>
            </div>
            <Switch
              checked={wc.show_avatar}
              onCheckedChange={(v) => updW("show_avatar", v)}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <div className="text-sm font-medium">Ping user</div>
              <div className="text-xs text-muted-foreground">
                Bot mentions the user outside the embed.
              </div>
            </div>
            <Switch
              checked={wc.ping_user}
              onCheckedChange={(v) => updW("ping_user", v)}
            />
          </div>
        </div>
      </Section>

      {/* GOODBYE */}
      <Section
        icon={UserMinus}
        title="Goodbye (leave)"
        description="When a member leaves, the bot posts a short message."
        enabled={gb.enabled}
        onToggle={(v) => updG("enabled", v)}
      >
        <Field label="Channel">
          <ChannelPicker
            channels={channels}
            value={gb.channel_id}
            onChange={(v) => updG("channel_id", v === "__none__" ? "" : v)}
          />
        </Field>

        <div className="grid md:grid-cols-[1fr_auto] gap-4">
          <Field label="Message" hint={PLACEHOLDERS_HINT}>
            <Textarea
              value={gb.message}
              onChange={(e) => updG("message", e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="**{user}** left the server. 👋"
            />
          </Field>
          <Field label="Color">
            <input
              type="color"
              value={gb.color || "#ED4245"}
              onChange={(e) => updG("color", e.target.value)}
              className="h-10 w-16 rounded-md border border-border bg-background cursor-pointer"
            />
          </Field>
        </div>
      </Section>

      {/* AUTOROLE */}
      <Section
        icon={UserCog}
        title="Auto-role"
        description="The bot assigns role(s) to every new member automatically."
        enabled={ar.enabled}
        onToggle={(v) => updA("enabled", v)}
      >
        <Field
          label="Roles on join"
          hint="You can select multiple; all are applied."
        >
          <MultiSelect
            options={roles}
            value={ar.role_ids}
            onChange={(v) => updA("role_ids", v)}
            prefix="@"
            placeholder="+ add role"
          />
        </Field>
      </Section>
    </div>
  );
}
